import { ApiAuthenticationFailedException } from '../errors'
import { buildQueryString } from '../utils/buildQueryString'
import { createRestApiClient } from '../utils/createRestApiClient'
import { sign } from '../utils/sign'
import { binanceAccountCategory } from './constants'

const BASE_URL = 'https://api.binance.com'
const COIN_M_BASE_URL = 'https://dapi.binance.com'
const USD_M_BASE_URL = 'https://fapi.binance.com'
const RECV_WINDOW = 5000 // 5秒の有効期間

const createAuthHeaders = async (params: {
  /** 認証用 API Key */
  apiKey: string
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
}): Promise<Record<string, string>> => {
  const { apiKey, createHeaders } = params

  // 強制適用する request headers 作成
  const overrideHeaders = createHeaders ? await createHeaders() : {}

  return {
    'Content-Type': 'application/x-www-form-urlencoded',
    'X-MBX-APIKEY': apiKey,
    ...overrideHeaders,
  }
}

export const createClient = ({
  accountCategory = binanceAccountCategory,
  apiKey,
  apiSecret,
  noAuth = false,
  createHeaders,
  proxy,
}: {
  /** 認証失敗エラーに設定するアカウントカテゴリ ID (default: binance) */
  accountCategory?: string
  /** 認証用 API Key */
  apiKey: string
  /** 認証用 API Secret */
  apiSecret: string
  /** 認証なしで実行する場合は noAuth を true に設定 */
  noAuth?: boolean
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
  /** request を proxy する場合の proxy base URL */
  proxy?: string
}) =>
  createRestApiClient({
    buildRequestData: async ({ method, path, query, body }) => {
      let baseUrl: string
      if (path.startsWith('/dapi')) {
        baseUrl = COIN_M_BASE_URL
      } else if (path.startsWith('/fapi')) {
        baseUrl = USD_M_BASE_URL
      } else {
        baseUrl = BASE_URL
      }
      if (noAuth) {
        const queryString = buildQueryString({
          ...query,
          ...body,
        })
        switch (method) {
          case 'GET': {
            return {
              url: `${baseUrl}${path}${queryString ? `?${queryString}` : ''}`,
              headers: createHeaders ? await createHeaders() : undefined,
            }
          }
          case 'POST': {
            return {
              url: `${baseUrl}${path}`,
              body: queryString,
              headers: createHeaders ? await createHeaders() : undefined,
            }
          }
          default: {
            throw new Error(`Unsupported method: ${method satisfies never}`)
          }
        }
      }

      const timestamp = Date.now().toString()
      const recvWindow = RECV_WINDOW.toString()

      // パラメータの準備
      const params = {
        ...query,
        ...body,
        recvWindow,
        timestamp,
      }

      const dataString = buildQueryString(params)

      // HMAC-SHA256署名の生成
      const signature = await sign({
        algorithm: 'sha256',
        payload: dataString,
        secret: apiSecret,
        encoding: 'hex',
      })

      const headers = await createAuthHeaders({
        apiKey,
        createHeaders,
      })

      if (method === 'GET') {
        // GETの場合: query parametersにsignatureを追加
        const queryWithSignature = buildQueryString({ ...params, signature })
        const url = `${baseUrl}${path}?${queryWithSignature}`
        return {
          url,
          headers,
        }
      }
      // POSTの場合: bodyにsignatureを追加
      const bodyWithSignature = buildQueryString({ ...params, signature })
      const url = `${baseUrl}${path}`
      return {
        url,
        headers,
        body: bodyWithSignature,
      }
    },
    getDelayForRetry: ({ statusCode, errorMessage }) => {
      // 400
      if (statusCode === 400) {
        // Timestamp for this request is outside of the recvWindow / Timestamp for this request was 1000ms ahead of the server's time エラーは 1秒待機してリトライ
        if (
          errorMessage.includes(
            'Timestamp for this request is outside of the recvWindow',
          ) ||
          errorMessage.includes(
            "Timestamp for this request was 1000ms ahead of the server's time",
          )
        ) {
          return 1000
        }
        // Invalid Api-Key エラーは認証失敗エラーを投げる
        if (errorMessage.includes('Invalid Api-Key')) {
          throw new ApiAuthenticationFailedException(accountCategory)
        }
      }
      // 401 エラーは 1秒待機してリトライ
      if (statusCode === 401) {
        // ただし、Invalid API-key, IP, or permissions for action エラーの場合はリトライしない
        if (
          errorMessage.includes(
            'Invalid API-key, IP, or permissions for action',
          )
        ) {
          return undefined
        }
        return 1000
      }
      // 429 エラーは 10秒待機してリトライ
      if (statusCode === 429) {
        return 10_000
      }
      // 5xx エラーは 1秒待機してリトライ
      if (statusCode >= 500) {
        return 1000
      }
      // それ以外のエラーはリトライしない
      return undefined
    },
    proxy,
  })
